import { useMutation } from '@apollo/client';
import EnhancedStepper from '@components/stepper/EnhancedStepper';
import { EnhancedButtonStatus } from '@components/_old/EnhancedButton';
import { useAppDispatch } from '@redux/hooks';
import { MAIN_THEME } from '@styles';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { getBusinessUserDetailsInfo } from '../../queries';
import { activateBusinessUser, deactivateBusinessUser } from './queries';
import { OpenConfirmationAction } from '@redux/confirmation/actions';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { getError } from '@utils/graph-utils';
import { toast } from 'react-toastify';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { isEmpty } from '@utils/validationUtils';
import { IConfirmation } from '@redux/confirmation/types';

interface IStepper {
  businessUserStatus: string;
}

const useStyles = makeStyles()(() => ({
  stepper: {
    backgroundColor: 'transparent',
    maxWidth: '90%',
    marginBottom: '20px',
    '& .MuiStepLabel-root .Mui-active, .MuiStepLabel-root .Mui-completed': {
      color: MAIN_THEME.color.black,
    },
    '@media only screen and (min-width: 600px)': {
      maxWidth: '1300px',
    },
  },
}));

const Stepper: React.FC<IStepper> = ({ businessUserStatus }) => {
  const { id: entityId } = useParams<{ id: string }>();
  const { classes } = useStyles();
  const dispatch = useAppDispatch();

  const [activateAction] = useMutation(activateBusinessUser, {
    variables: { entityId },
    errorPolicy: 'all',
    refetchQueries: [getBusinessUserDetailsInfo],
  });

  const [deactivateAction] = useMutation(deactivateBusinessUser, {
    variables: { entityId },
    errorPolicy: 'all',
    refetchQueries: [getBusinessUserDetailsInfo],
  });

  const [activateButtonState, setActivateButtonState] =
    useState<EnhancedButtonStatus>();
  const [deactivateButtonState, setDeactivateButtonState] =
    useState<EnhancedButtonStatus>();

  const steps =
    businessUserStatus === 'new'
      ? ['New', 'Active']
      : ['New', businessUserStatus === 'active' ? 'Active' : 'Inactive'];
  const getBusinessUserStatus = (steps: string[], status: string) => {
    switch (status) {
      case 'active':
        return steps[1];
      case 'inactive':
        return steps[1];
      default:
        return steps[0];
    }
  };

  const handleActivateOrDeactivateBusinessUser = () => {
    const isActive = businessUserStatus === 'active';
    if (isActive) {
      const confirmation: IConfirmation = {
        open: true,
        title: 'Deactivate',
        message: `Are you sure you want to deactivate this business user?`,
        callback: async () => {
          setDeactivateButtonState('loading');
          try {
            const result = await deactivateAction();
            if (isEmpty(result.errors)) {
              setDeactivateButtonState('success');
              toast.success(
                <ToastSuccessMessage>
                  Business user successfully deactivated.
                </ToastSuccessMessage>
              );
            } else {
              setDeactivateButtonState(undefined);
              toast.error(
                <ToastErrorMessage>{getError(result)}</ToastErrorMessage>
              );
            }
          } finally {
            setTimeout(() => {
              setDeactivateButtonState(undefined);
            }, 1000);
          }
        },
        submitButtonText: 'Yes',
        cancelButtonText: 'No',
      };
      dispatch(OpenConfirmationAction(confirmation));
    } else {
      const confirmation: IConfirmation = {
        open: true,
        title: 'Activate',
        message: `Are you sure you want to activate this business user?`,
        callback: async () => {
          setActivateButtonState('loading');
          try {
            const result = await activateAction();
            if (isEmpty(result.errors)) {
              setActivateButtonState('success');
              toast.success(
                <ToastSuccessMessage>
                  Business user successfully activated.
                </ToastSuccessMessage>
              );
            } else {
              setActivateButtonState(undefined);
              toast.error(
                <ToastErrorMessage>{getError(result)}</ToastErrorMessage>
              );
            }
          } finally {
            setTimeout(() => {
              setActivateButtonState(undefined);
            }, 1000);
          }
        },
        submitButtonText: 'Yes',
        cancelButtonText: 'No',
      };
      dispatch(OpenConfirmationAction(confirmation));
    }
  };

  return (
    <div style={{ marginTop: '20px' }}>
      <EnhancedStepper
        activeStep={getBusinessUserStatus(steps, businessUserStatus)}
        className={classes.stepper}
        steps={steps}
        showStepperButton
        buttonTitle={
          businessUserStatus === 'active' ? 'Deactivate' : 'Activate'
        }
        buttonState={
          businessUserStatus === 'active'
            ? deactivateButtonState
            : activateButtonState
        }
        buttonOnClick={handleActivateOrDeactivateBusinessUser}
      />
    </div>
  );
};

export default Stepper;
